import { ContextMenuItem } from "lib/@components/context-menu-bar/context-menu-item"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useDialog } from "lib/@hooks/useDialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { Secure } from "lib/authorization/secure"
import { AUTHORING_MANAGE_LEGISLATION_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { updateAuthoringLegislation } from "../controller/update-authoring-legislation"
import { LegislationLibraryEditDialog } from "./legislation-library-edit-dialog"
import {
    LEGISLATION_FOLDER,
    confirmLegislationUrlChange,
    resolveDestinationId,
    saveFolderEdit,
    applyLegislationFolderDiff,
} from "../legislation-library-helpers"

export function LegislationLibraryEditMenuItem({ item }) {
    const { library } = useBoundContext()
    const isFolder = item?.type === LEGISLATION_FOLDER
    const openEditDialog = useDialog(
        <Bound library={library}>
            <LegislationLibraryEditDialog node={item} onSubmit={isFolder ? submitFolder : undefined} />
        </Bound>,
        { maxWidth: "sm", fullWidth: true }
    )

    return (
        <Secure demand={AUTHORING_MANAGE_LEGISLATION_DEMAND} fallback={<></>}>
            <ContextMenuItem
                icon="mdi:pencil-outline"
                label="Edit"
                onClick={handleEdit}
                priority={10}
                data-cy="legislation-library-edit-menu-item"
            />
        </Secure>
    )

    async function handleEdit() {
        try {
            const result = await openEditDialog()
            if (!result) return

            if (isFolder) {
                successSnackbar("Folder updated")
            } else {
                const urlChanged = (result.url ?? "") !== (item.url ?? "")
                if (urlChanged) {
                    const confirmed = await confirmLegislationUrlChange({
                        scheduleCount: item.scheduleCount,
                        previousUrl: item.url,
                        nextUrl: result.url,
                    })
                    if (!confirmed) return
                }

                const { title, description, folderIds } = result
                const legislation = {
                    title,
                    description,
                    folderIds,
                    ...(urlChanged ? { url: result.url } : {}),
                }
                await updateAuthoringLegislation({ id: item.id, legislation })
                applyLegislationFolderDiff(library.state, { legislationId: item.id, folderIds })
                successSnackbar("Legislation updated")
            }
        } catch (e) {
            console.error(e)
            errorSnackbar("Failed to save changes")
        }
    }

    async function submitFolder({ name, destinationParentId }) {
        const { folder } = await saveFolderEdit({
            id: item.id,
            name,
            destinationParentId: resolveDestinationId(destinationParentId),
        })
        library.setFolder(folder)
        return folder
    }
}
